import { MusicXmlHtmlPage, MusicXmlRenderer, MusixXmlSheet } from "../audio/MusicXmlRenderer.js";
import { Sequence } from "../audio/Sequence.js";
import { VOEmoji } from "../model/DataStructure.js";
import { MenuBar } from "./MenuBar.js";
import { TimelineItem } from "./TimelineItem.js";

export interface SheetOptions {
    container: HTMLElement;
    sequence: Sequence<TimelineItem>;
    visible: boolean;
    emojis: VOEmoji[];
    menuBar: MenuBar;
    collapsable: boolean;
}

export class Sheet {

    private onVisibleChanged = e5.Signal.create<[Sheet]>();
    private onPageChange = e5.Signal.create<[Sheet]>();
    private onProgressChange = e5.Signal.create<[Sheet]>();

    private _element: HTMLElement;
    private _sequence: Sequence<TimelineItem>;
    private _musicXmlRenderer: MusicXmlRenderer;
    private _view: e5.ScrollView;
    private _pageHeight = 1448.2285714285715;
    private _pageWidth = 1024;
    private _viewContent: HTMLElement;
    private _pageIndex: number = 0;
    private _pages: MusicXmlHtmlPage[] = [];
    private _progress: boolean = false;
    private _pageMenu: MenuBar;
    private _mainMenu: MenuBar;

    constructor() {
    }

    public async create(options: SheetOptions) {
        options = e5.object.merge({

        }, options);

        this._element = document.createElement("div");
        this._element.className = "sheet e5-hidden";
        this._element.innerHTML = `
        <div class="sheet__content-wrap">
            <div class="sheet__content">
                <div class="sheet__header">
                    <div class="sheet__button-group">
                        <button class="sheet__button sheet__button--active sheet__button--progress e5-hidden"><i class="fal fa-spinner fa-pulse"></i></button>
                    </div>
                </div>
                <div class="sheet__body">
                    <div class="sheet__view"></div>    
                    <div class="sheet__thumbnail-list e5-hidden"></div>
                </div>
                <div class="sheet__footer"></div>
            </div>
        </div>
        `;

        this._mainMenu = new MenuBar({
            container: this._element.querySelector(".sheet__header")
        });

        this._mainMenu.add({
            icon: "fal fa-spinner fa-pulse",
            visible: this.progress,
            onInit: (button) => {
                this.onProgressChange.add(() => {
                    button.visible = this.progress;
                });
            }
        });

        this._mainMenu.add({
            icon: "fal fa-gallery-thumbnails",
            tooltip: {
                default: "Vorschaubilder anzeigen",
                active: "Vorschaubilder ausblenden"
            },
            onTrigger: (button) => {
                this._element.classList.toggle("sheet--thumbnail-list");
            }
        });

        this._mainMenu.add({
            icon: "fal fa-file-pdf",
            tooltip: {
                default: "PDF herunterladen",
                disabled: "PDF wird erstellt..."
            },
            onTrigger: async (button) => {
                button.disabled = true;
                const res = await this._musicXmlRenderer.draw({
                    sheets: this.getDrawItems(),
                    createPdf: true,
                    abortable: false
                });

                e5.FileSaver.saveBlob(
                    res.pdf.output("blob"),
                    {
                        name: "signum-emoji-music-sheet.pdf"
                    }
                );

                button.disabled = false;
            }
        });

        if (options.collapsable) {
            this._mainMenu.add({
                icon: {
                    default: "fal fa-expand",
                    active: "fal fa-compress"
                },
                onInit: (button) => {
                    // this.onVisibleChanged.add((sheet) => {
                    //     button.active = sheet.visible;
                    // });
                },
                onTrigger: (button) => {
                    this._element.classList.toggle("sheet--fullscreen");
                    button.active = this._element.classList.contains("sheet--fullscreen");
                }
            });
        } else {
            this._element.classList.add("sheet--fullscreen");
        }

        this._mainMenu.add({
            icon: "fal fa-times",
            onTrigger: (button) => {
                this.visible = false;
            }
        });

        this._pageMenu = new MenuBar({
            container: this._element.querySelector(".sheet__footer")
        });

        this._pageMenu.add({
            icon: "fal fa-chevron-left",
            tooltip: "Vorherige Seite",
            disabled: true,
            onInit: (button) => {
                this.onPageChange.add(() => {
                    button.disabled = !this._pages[this._pageIndex] || this._pageIndex <= 0;
                });
            },
            onTrigger: () => {
                this.showPage(this._pageIndex - 1);
            }
        });

        this._pageMenu.add({
            visible: false,
            onInit: (button) => {
                this.onPageChange.add(() => {
                    const label = `${this._pageIndex + 1} / ${this._pages.length}`;
                    button.label = label;
                    button.visible = !!label;
                });
            }
        });

        this._pageMenu.add({
            icon: "fal fa-chevron-right",
            tooltip: "Nächste Seite",
            disabled: true,
            onInit: (button) => {
                this.onPageChange.add(() => {
                    button.disabled = !this._pages[this._pageIndex] || this._pageIndex >= this._pages.length - 1;
                });
            },
            onTrigger: () => {
                this.showPage(this._pageIndex + 1);
            }
        });

        this._sequence = options.sequence;
        this._musicXmlRenderer = new MusicXmlRenderer();

        this._viewContent = document.createElement("div");
        this._viewContent.className = "sheet__view-content e5-hidden";

        this._view = new e5.ScrollView({
            doubleClickZoom: true
        });
        this._view.resizeBehaviour = e5.ScrollViewResize.ContainMaxOriginal;
        // this._view.wheel = e5.ScrollViewWheel.CtrlZoom;
        this._view.content = {
            element: this._viewContent,
            width: this._pageWidth,
            height: this._pageHeight
        };
        this._element.querySelector(".sheet__view")
            .appendChild(this._view.element);

        this._sequence.onChangeItems.debounce(
            () => {
                if (!this.visible) {
                    return;
                }
                this.renderSheet();
            }, 750);
        this._sequence.onChangeItems.add(() => {
            if (!this.visible) {
                return;
            }
            this.progress = true;
        });

        options.container.appendChild(this._element);

        const onResizeThumbnailList = e5.Resize.create(this._element.querySelector<HTMLElement>(".sheet__thumbnail-list"));
        onResizeThumbnailList.add(() => {
            this.resizeThumbnailList();
        });

        e5.event.on({
            type: "click",
            selector: `[data-page-index]`,
            passive: true,
            scope: this._element,
            listener: (e) => {
                this.showPage(parseInt(e.target.getAttribute("data-page-index")));
            }
        });

        await e5.wait(0);

        this.visible = options.visible;

        options.menuBar.add({
            icon: "fal fa-music-note",
            tooltip: {
                default: "Musiknoten anzeigen",
                active: "Musiknoten ausblenden"
            },
            disabled: this._sequence.items.length === 0,
            active: this.visible,
            onInit: (button) => {
                this._sequence.onChangeItems.add(() => {
                    button.disabled = this._sequence.items.length === 0;
                });
                this.onVisibleChanged.add((sheet) => {
                    button.active = sheet.visible;
                });
            },
            onTrigger: (button) => {
                this.visible = !button.active;
            }
        });

        return this;
    }

    private get visible(): boolean {
        return !this._element.classList.contains("e5-hidden");
    }

    private set visible(value: boolean) {
        if (this.visible == value) {
            return;
        }
        this._element.classList.toggle("e5-hidden", !value);

        if (value) {
            this.resizeThumbnailList();
            this.renderSheet();
        }

        this.onVisibleChanged.dispatch(this);
    }


    private get progress(): boolean {
        return this._progress;
    }

    private set progress(value: boolean) {
        if (this._progress == value) {
            return;
        }
        this._progress = value;
        this.onProgressChange.dispatch(this);
    }

    private resizeThumbnailList(): void {
        const list = this._element.querySelector<HTMLElement>(".sheet__thumbnail-list");

        const cs = getComputedStyle(list);
        const listWidth = list.clientWidth - (parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight));
        const listHeight = list.offsetHeight;

        list.style.setProperty("--scale-x", (listWidth / this._pageWidth) + "");
        list.style.setProperty("--scale-y", (listHeight / this._pageHeight) + "");
    }

    private async renderSheet() {

        const docHolder = this._element.querySelector<HTMLElement>(".sheet__thumbnail-list");
        this.progress = true;

        if (this._sequence.items.length === 0) {
            docHolder.innerHTML = "";
            this.updatePages([]);
            return;
        }

        const res = await this._musicXmlRenderer.draw({
            sheets: this.getDrawItems(),
            createPdf: false,
            onProgress: (e) => {
                // console.log(((e.loaded / e.total) * 100).toFixed(0));
            }
        });

        if (!res) {
            this.updatePages([]);
            return;
        }

        this.updatePages(res.htmlPages);
    }

    private getDrawItems() {
        let drawItems: MusixXmlSheet[] = [];
        for (let item of this._sequence.items) {
            const key = `${item.emoji.id}-${item.emoji.title}-${item.speed}-${item.pitch}`;

            let pitch = item.pitch;
            let info = Math.round(item.emoji.melody.bpm * item.speed) + " bpm";

            if (item.emoji.melody_kind === "silence") {
                pitch = 0;
                info = (item.emoji.silence_duration * item.speed).toFixed(1) + " Sek.";
            }
            drawItems.push({
                xmlString: item.emoji.musicXmlString,
                label: item.emoji.name,
                info: info,
                transpose: pitch,
                key: key,
                icon: item.emoji.lottieCanvas,
            });
        }
        return drawItems;
    }

    private showPage(pageIndex: number): void {
        pageIndex = e5.Calc.clamp(pageIndex, 0, this._pages.length - 1);
        this._pageIndex = pageIndex;

        let p = this._pages[pageIndex];
        this._viewContent.innerHTML = "";
        if (p) {
            const e = p.element.cloneNode(true) as HTMLElement;
            e.className = "";
            this._viewContent.appendChild(e);
            this._viewContent.classList.remove("e5-hidden");
        } else {
            this._viewContent.classList.add("e5-hidden");
        }

        this.onPageChange.dispatch(this);
    }

    private updatePages(pages: MusicXmlHtmlPage[]) {
        if (!pages) {
            pages = [];
        }

        this._pages = pages.slice();

        const list = this._element.querySelector<HTMLElement>(".sheet__thumbnail-list");
        list.innerHTML = "";

        list.classList.toggle("e5-hidden", pages.length === 0);
        this.progress = false;

        if (pages.length === 0) {
            return;
        }

        let index = 0;
        for (let p of pages) {

            let thumbnail = document.createElement("div");
            thumbnail.className = "sheet__thumbnail";
            thumbnail.style.aspectRatio = `${p.width} / ${p.height}`;
            thumbnail.setAttribute("data-page-index", index + "");
            // thumbnail.style.width = `${p.width * scale}px`;
            // thumbnail.style.height = `${p.height * scale}px`;

            let pa = p.element.cloneNode(true) as HTMLElement;
            pa.className = "sheet__thumbnail-content";
            thumbnail.appendChild(pa);

            list.appendChild(thumbnail);
            index++;
        }

        this.showPage(this._pageIndex);
    }
}